import { useLocale, useTranslation } from '@multaqa/i18n'
import { Panel, PanelContent } from '@multaqa/ui/components/ui/panel'
import { Skeleton } from '@multaqa/ui/components/ui/skeleton'
import { InlineQueryError } from '@/components/inline-query-error'
import { localizedText } from '@/lib/localized-text'
import type { GroupRow } from '@/lib/platform'
import { slotLimit, useGroupEntitlements, usePlanCatalogue } from './api'
import { GroupLimits } from './group-detail-limits'
import { GroupModules } from './group-detail-modules'

function isModuleRow(key: string): boolean {
  return key.startsWith('module.')
}

/**
 * The group's commercial context: which plan it sits on, the company slots
 * that plan's `companies.max` entitlement allows, and every effective
 * entitlement (override → plan) split into two cards — modules (a switch
 * per `module.*` key) and limits (everything else) — rather than one flat
 * table of raw keys.
 */
export function GroupEntitlements({ group }: { group: GroupRow }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const plans = usePlanCatalogue()
  const entitlements = useGroupEntitlements(group.id)
  const rows = entitlements.data ?? []
  const moduleRows = rows.filter((row) => isModuleRow(row.key))
  const limitRows = rows.filter((row) => !isModuleRow(row.key))
  const planRows = plans.data ?? []
  const plan = planRows.find((candidate) => candidate.id === group.plan?.id) ?? null
  const limit = slotLimit(group, planRows)

  return (
    <Panel data-testid="group-plan-panel">
      <PanelContent className="space-y-6">
        <section aria-labelledby="group-plan-heading">
          <h2 id="group-plan-heading" className="text-h3 mb-3">
            {t('groups.detail.plan.title')}
          </h2>
          {plans.isLoading ? <Skeleton className="h-6 w-48" /> : null}
          {plan ? (
            <p className="text-body" data-testid="group-detail-plan-line">
              {localizedText(plan.name, locale)} {'· '}
              {t('groups.detail.plan.slotsLine', {
                count: Number(group.company_count),
                limit: limit === null ? t('groups.slots.unlimited') : limit,
              })}
            </p>
          ) : !plans.isLoading ? (
            <p className="text-small text-muted-foreground">{t('groups.noPlan')}</p>
          ) : null}
          {plans.isError ? (
            <InlineQueryError
              title={t('error.asyncTitle')}
              description={t('error.asyncBody', { title: t('groups.detail.plan.title') })}
              retryLabel={t('action.retry')}
              onRetry={() => void plans.refetch()}
            />
          ) : null}
        </section>
        {entitlements.isLoading ? <Skeleton className="h-24 w-full" /> : null}
        {entitlements.isError ? (
          <InlineQueryError
            title={t('error.asyncTitle')}
            description={t('error.asyncBody', { title: t('groups.tabs.entitlements') })}
            retryLabel={t('action.retry')}
            onRetry={() => void entitlements.refetch()}
          />
        ) : null}
        {!entitlements.isLoading && !entitlements.isError ? (
          <>
            <GroupModules groupId={group.id} rows={moduleRows} />
            <GroupLimits groupId={group.id} rows={limitRows} />
          </>
        ) : null}
      </PanelContent>
    </Panel>
  )
}
